/* eslint-disable camelcase */
/* eslint-disable jsx-a11y/label-has-associated-control */
import React, { memo, useState, useEffect, useMemo, useRef } from 'react';
import { enableES5 } from 'immer';
import { useDispatch, useSelector } from 'react-redux';
import { Modal, Button, Tooltip, OverlayTrigger } from 'react-bootstrap';
import classNames from 'classnames';
import xhr from '../../../utils/xhr';
import utils from '../../../utils/utils';
import { showNotification } from '../../../redux/notification';
import BaseModal from '../BaseModal/BaseModal';
import { user } from '../../../redux/user';
import FlexRequestShiftConfirmationModal from '../FlexRequestShiftConfirmationModal/FlexRequestShiftConfirmationModal';
import ConfirmationModalNew from '../ConfirmationModalNew';
import facilityInfoIcon from '../../../assets/images/facility-info-icon.svg';
import { ReactComponent as RefreshIcon } from '../../../assets/images/refresh-purple.svg';
import Select from '../../form/Select';
import useStateWithCallback from '../../../hooks/useStateWithCallback';
import useRequestShifts, { computeTotalShiftsToCreate } from '../../../hooks/useRequestShifts';
import useMediaQuery, { MEDIUM_SCREEN_WIDTH_QUERY } from '../../hooks/useMediaQuery';
import ConfirmationModal from '../ConfirmationModal';
import ShiftsConfirmation from '../../../features/shiftsToRequest/ShiftsConfirmation';
import PERMISSION from '../../../constants/Permissions';
import LoadingSpinner from '../../spinner/LoadingSpinner';
import RequestedShiftGroup from '../../../features/shiftsToRequest/RequestedShiftGroup';

import './SelectFacilityRequestShiftsModal.css';

enableES5();

const SelectFacilityRequestShiftsModal = (props) => {
	const userData = useSelector(user);
	const { permissions, permissionsByFacility } = userData;
	const dispatch = useDispatch();

	const isMobile = useMediaQuery(MEDIUM_SCREEN_WIDTH_QUERY);
	const [selectedFacility, setSelectedFacility] = useState();
	const [showLoadingSpinner, setShowLoadingSpinner] = useStateWithCallback(false);
	const [showChangingFacilityModal, setShowChangingFacilityModal] = useState(false);
	const [needMoreShifts, setNeedMoreShifts] = useState(false);
	const [facilityToLoad, setFacilityToLoad] = useState();
	const [currentShiftStructures, setCurrentShiftStructures] = useState([]);
	const [currentFacilityUnits, setCurrentFacilityUnits] = useState([]);
	const shiftsSectionRef = useRef();

	const { shiftStructures, isGlobalCalendar } = props;

	const {
		initialShiftGroups,
		shiftsGroups,
		setShiftsGroups,
		errors,
		handleBackendErrors,
		updateShift,
		removeShift,
		addEmptyShiftGroup,
		showConfirmationModal,
		setShowConfirmationModal,
		showFlexStructuresAlreadyStartedModal,
		setShowFlexStructuresAlreadyStartedModal,
		checkIntegrity,
		checkIfCanAddTodayShiftForStructure,
		getShiftsToRequest,
		reset
	} = useRequestShifts(userData, currentShiftStructures);

	const postShiftsRequested = async () => {
		setShowLoadingSpinner(true, async () => {
			const currentFacility = selectedFacility.id;
			try {
				props.setIsLoading(true);
				let postUrl = '/shifts';
				const shiftsToRequest = getShiftsToRequest(shiftsGroups, props.isFlex);
				let payload = {
					facilityId: currentFacility,
					shiftsData: shiftsToRequest
				};
				if (props.isFlex) {
					postUrl = `/flex/facility/${currentFacility}/shifts`;
					payload = shiftsToRequest;
				}

				if (props.isHybrid) {
					postUrl = `/hybrid/facility/${currentFacility}/shifts`;
					payload = shiftsToRequest;
				}
				if (props.isOnCall) {
					postUrl = `/on-call/facility/${currentFacility}/shifts`;
					payload = {
						facilityId: currentFacility,
						onCall: true,
						shiftsData: shiftsToRequest
					};
				}
				const shiftsPosted = await xhr.request('POST', postUrl, payload);
				if (shiftsPosted) {
					dispatch(showNotification('success', 'Shifts requested successfully'));
					props.updateFeed();
					if (needMoreShifts) {
						reset();
					} else {
						props.onClose();
					}
				}
			} catch (e) {
				if (e.response.status === 403) {
					dispatch(
						showNotification(
							'error',
							'Shift could not be added as you do not have permission to add shifts at this facility.'
						)
					);
					props.onClose();
				} else {
					const errorMessage = utils.handleNetworkErrorMessage(e, 'Failed to save requested shifts. Please try again.');
					if (e.response && e.response.data) {
						handleBackendErrors(e.response.data.errors);
					}
					dispatch(showNotification('error', errorMessage));
				}
			} finally {
				setShowConfirmationModal(false);
				setShowFlexStructuresAlreadyStartedModal(false);
				setShowLoadingSpinner(false);
				props.setIsLoading(false);
			}
		});
	};

	const triggerRecurringShiftsModal = () => {
		if (props.user.isRecurringScheduleEnabled) {
			props.onClose();
			props.setShowRecurringShiftsModal(true);
		} else {
			dispatch(
				showNotification(
					'error',
					'This feature is coming soon. If you would like early access, please email PortalSupport@ShiftMed.com'
				)
			);
		}
		return true; // placeholder for sonarcloud
	};

	const showUnits = useMemo(() => {
		if (!currentFacilityUnits?.length) {
			return false;
		}
		// if the facility doesn't have portal units enabled, permissions don't matter anymore
		if (!selectedFacility?.portal_units_enabled) {
			return false;
		}
		if (isGlobalCalendar) {
			const facilityPermissions = permissionsByFacility[selectedFacility?.id];
			return utils.isAuthorizedExact(
				props.isFlex ? PERMISSION.FLEX_ASSIGN_UNITS : PERMISSION.EDIT_ASSIGNMENT,
				facilityPermissions
			);
		}
		return utils.isAuthorizedExact(
			props.isFlex ? PERMISSION.FLEX_ASSIGN_UNITS : PERMISSION.EDIT_ASSIGNMENT,
			permissions
		);
	}, [permissions, selectedFacility, permissionsByFacility, props.isFlex]);

	const updateCurrentShiftsStructures = (facilityId) => {
		const _structures = shiftStructures
			.filter((structure) => structure.facility_id === facilityId)
			.sort((a, b) => {
				const aFromTime = utils.convertTimeStringToSeconds(a.from_time);
				const bFromTime = utils.convertTimeStringToSeconds(b.from_time);
				if (aFromTime < bFromTime) {
					return -1;
				}
				if (aFromTime > bFromTime) {
					return 1;
				}
				let aToTime = utils.convertTimeStringToSeconds(a.to_time);
				if (aFromTime > aToTime) {
					aToTime += 24 * 3600;
				}
				let bToTime = utils.convertTimeStringToSeconds(b.to_time);
				if (bFromTime > bToTime) {
					bToTime += 24 * 3600;
				}
				if (aToTime < bToTime) {
					return -1;
				}
				if (aToTime > bToTime) {
					return 1;
				}
				return 0;
			});
		setCurrentShiftStructures(_structures);
	};

	const handleChangeFacility = () => {
		setShiftsGroups(initialShiftGroups);
		const facility = props.facilities.find((f) => f.value === facilityToLoad);
		setSelectedFacility(facility);
		updateCurrentShiftsStructures(facilityToLoad);
		setCurrentFacilityUnits(facility.units || []);
		setFacilityToLoad(null);
		setShowChangingFacilityModal(false);
	};

	const handleSelectFacility = (e) => {
		const existChanges = shiftsGroups.some((s) => !!s.resourceType);
		const currentFacility = selectedFacility?.id;
		if (currentFacility && currentFacility !== e.facility && existChanges) {
			setShowChangingFacilityModal(true);
			setFacilityToLoad(e.facility);
		} else {
			const facility = props.facilities.find((f) => f.value === e.facility);
			updateCurrentShiftsStructures(e.facility);
			setCurrentFacilityUnits(facility.units);
			setSelectedFacility(facility);
		}
	};

	useEffect(() => {
		if (props.facilities.length === 1) {
			const [activeFacility] = props.facilities;
			setSelectedFacility(activeFacility);
			setCurrentFacilityUnits(activeFacility.units || []);
			updateCurrentShiftsStructures(activeFacility.value);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [props.facilities]);

	const toggleNeedMoreShifts = () => setNeedMoreShifts(!needMoreShifts);

	const previousShiftsGroupsLengthRef = useRef(shiftsGroups.length);
	useEffect(() => {
		if (shiftsGroups.length > previousShiftsGroupsLengthRef.current) {
			shiftsSectionRef.current?.lastElementChild?.scrollIntoView({ behavior: 'smooth' });
		}
		previousShiftsGroupsLengthRef.current = shiftsGroups.length;
	}, [shiftsGroups.length]);

	return [
		<Modal
			key="select-facility-shift-requests-modal"
			size="xl"
			show={!showConfirmationModal && !showFlexStructuresAlreadyStartedModal && !showChangingFacilityModal}
			aria-labelledby="contained-modal-title-vcenter"
			centered
			className="select-facility-shift-requests-modal"
			onHide={props.onClose}
			backdrop="static"
			keyboard={false}
		>
			<Modal.Header closeButton className="modal-request-header">
				<Modal.Title id="contained-modal-title-vcenter" className="request-shift-title">
					<span>{props.title || 'Single shifts'}</span>
					{props.isHybrid && (
						<OverlayTrigger
							className="tooltip-component"
							placement={isMobile ? 'bottom' : 'right'}
							delay={{ show: 100, hide: 400 }}
							overlay={
								<Tooltip
									delay={{ show: 150, hide: 400 }}
									className={classNames('info-facility-tooltip', { 'is-bottom-placement': isMobile })}
								>
									Hybrid shifts are open to both Flex and ShiftMed professionals and are filled on a first come first
									serve basis. If you want to limit which group can claim, you can use the request On Demand or request
									Flex shift options.
								</Tooltip>
							}
						>
							<img className="tooltip-title" src={facilityInfoIcon} alt="info" />
						</OverlayTrigger>
					)}
				</Modal.Title>
				{!isMobile && !props.isFlex && !isGlobalCalendar && !props.isHybrid && !props.isOnCall && (
					<Button className="add-recurring-shifts-button" onClick={triggerRecurringShiftsModal} variant="secondary">
						<RefreshIcon />
						&nbsp; Switch to recurring shifts
					</Button>
				)}
			</Modal.Header>
			<Modal.Body className="request-modal-body">
				{props.isOnCall && (
					<div className="on-call-info-banner">
						<img className="tooltip-title" src={facilityInfoIcon} alt="info" />
						<span>On Call shifts are only available for On Demand shifts at this time.</span>
					</div>
				)}
				{isGlobalCalendar && (
					<div>
						<div className="section-header">Facility</div>
						<div className="select-facility-container">
							<div className="facility-label">
								Facility
								<OverlayTrigger
									className="tooltip-component"
									placement="right"
									delay={{ show: 100, hide: 400 }}
									overlay={
										<Tooltip delay={{ show: 150, hide: 400 }} className="info-facility-tooltip">
											This list will only include facilities where you have permission to request shifts. If the
											facility you need isn’t listed, please reach out to your account manager.
										</Tooltip>
									}
								>
									<img src={facilityInfoIcon} alt="info" />
								</OverlayTrigger>
							</div>
							<div>
								<Select
									containerClass="select-facility"
									name="facility"
									placeholder="Select..."
									className="select-facility-request-shift"
									isSearchable
									options={props.facilities}
									value={selectedFacility?.id}
									handleChange={handleSelectFacility}
									filterFn={(option, input) => {
										if (input) {
											return option.label.toLowerCase().includes(input.toLowerCase());
										}
										return true;
									}}
								/>
							</div>
						</div>
					</div>
				)}
				<div ref={shiftsSectionRef} className="shifts-section">
					<div className="section-header">Shift details and dates</div>
					<div className="section-info">Request up to 30 dates total with up to 20 professionals per shift.</div>
					{shiftsGroups.map((shiftsGroup, index) => (
						<RequestedShiftGroup
							key={`shift${shiftsGroup.id}`}
							availableShiftStructures={currentShiftStructures}
							allowRemove={shiftsGroups.length > 1}
							shiftsGroups={shiftsGroups}
							updateShift={updateShift}
							shiftIndex={index}
							errors={errors[shiftsGroup.id] || {}}
							removeShift={removeShift}
							shift={shiftsGroup}
							isFlex={props.isFlex}
							isHybrid={props.isHybrid}
							isGlobalCalendar={props.isGlobalCalendar}
							currentFacility={selectedFacility?.id}
							showUnits={showUnits}
							units={currentFacilityUnits}
							facilityPortalUnitsEnabled={selectedFacility?.portal_units_enabled}
							maxShiftsToCreate={userData.maxShiftsToCreate}
							checkIfCanAddTodayShiftForStructure={checkIfCanAddTodayShiftForStructure}
							isMobile={isMobile}
						/>
					))}
					<button
						type="button"
						className="add-shift-button"
						onClick={addEmptyShiftGroup}
						disabled={
							!selectedFacility?.id ||
							Math.max(computeTotalShiftsToCreate(shiftsGroups), shiftsGroups.length) >= userData.maxShiftsToCreate
						}
					>
						+ Add another shift
					</button>
				</div>
			</Modal.Body>
			<Modal.Footer>
				<div>
					<label className="need-more-shifts">
						<input
							type="checkbox"
							className="custom-checkbox-control"
							value={needMoreShifts}
							checked={needMoreShifts}
							onChange={toggleNeedMoreShifts}
						/>
						Need more shifts? Create another request
					</label>
				</div>
				<div className="buttons">
					<Button className="cancel-shifts-button" onClick={props.onClose} variant="secondary">
						{props.cancelLabel || 'Cancel'}
					</Button>
					<Button
						variant="primary"
						className="req-shifts-button"
						disabled={!selectedFacility}
						onClick={() => checkIntegrity(props.isFlex)}
					>
						{props.confirmLabel || 'Request shifts'}
					</Button>
				</div>
			</Modal.Footer>
		</Modal>,
		showConfirmationModal && props.isFlex && (
			<BaseModal
				confirmModal
				className="primary-colors rounded-modal"
				key="confirmation-modal-request-flex"
				title="Request Shifts?"
				primaryActionLabel="Request"
				onPrimaryAction={postShiftsRequested}
				secondaryActionLabel="Go Back"
				onSecondaryAction={() => setShowConfirmationModal(false)}
				onHide={() => setShowConfirmationModal(false)}
				primaryActionDisabled={showLoadingSpinner}
				secondaryActionDisabled={showLoadingSpinner}
			>
				Do you want to confirm this shift request?
			</BaseModal>
		),

		showConfirmationModal && !props.isFlex && (
			<ConfirmationModal
				className="confirmation-modal-request"
				key="confirmation-modal-request"
				size="xl"
				title={`Confirm ${props.isHybrid ? 'Hybrid shift' : ''}${props.isOnCall ? 'On Call shift' : ''} request `}
				scrollable
				customModalBodyClass="modal-body-shift-confirmation"
				onConfirm={postShiftsRequested}
				onCancel={() => setShowConfirmationModal(false)}
				closeButton={false}
				cancelLabel="Go Back"
				confirmLabel="Confirm"
			>
				{showLoadingSpinner && (
					<div className="spinner-wrapper">
						<LoadingSpinner />
					</div>
				)}
				<div className="facility-and-modal-container">
					<span>
						Facility: <span className="facility-name">{selectedFacility?.label || ''}</span>
					</span>
					<ShiftsConfirmation
						shiftsGroups={shiftsGroups}
						shiftStructures={shiftStructures}
						shiftsByDay={[]}
						facilityId={selectedFacility?.id}
						showUnits={showUnits}
						isHybrid={props.isHybrid}
						isOnCall={props.isOnCall}
					/>
					{(props.isHybrid || props.isOnCall) && (
						<div className="hybrid-guaranteed-info">
							<img className="tooltip-title" src={facilityInfoIcon} alt="info" />
							{props.isHybrid && (
								<span>Note: Guaranteed shifts only apply to shifts claimed by ShiftMed professionals.</span>
							)}
							{props.isOnCall && (
								<span>
									Note: This request will add On Call shifts that can only be claimed by On Demand professionals at this
									time
								</span>
							)}
						</div>
					)}
				</div>
			</ConfirmationModal>
		),

		showFlexStructuresAlreadyStartedModal && (
			<FlexRequestShiftConfirmationModal
				key="confirmation-modal-request-flex"
				title="Request shifts? Shifts have started."
				textContent="One or more of the shifts you selected have already started. Do you want to confirm this shift request?"
				boxTextContent={
					<>
						<strong>Reminder:</strong> Shift modifications must be made before their scheduled end time
					</>
				}
				primaryActionLabel="Request shifts"
				onPrimaryAction={postShiftsRequested}
				secondaryActionLabel="Go Back"
				onSecondaryAction={() => setShowFlexStructuresAlreadyStartedModal(false)}
				onHide={() => setShowFlexStructuresAlreadyStartedModal(false)}
				primaryActionDisabled={showLoadingSpinner}
				secondaryActionDisabled={showLoadingSpinner}
			/>
		),

		showChangingFacilityModal && (
			<ConfirmationModalNew
				key="change-facility-confirmation-modal"
				className="change-facility-confirmation-modal"
				title="Change facility?"
				confirmLabel="Change facility"
				onConfirm={handleChangeFacility}
				cancelLabel="Go back"
				onCancel={() => setShowChangingFacilityModal(false)}
				showLoadingSpinner={showLoadingSpinner}
			>
				<>
					<p>Your shift request has not been completed.</p>
					<div className="change-facility-secondary-text">
						If you switch to a different facility now, your current shift request <strong>will be discarded</strong>.
					</div>
				</>
			</ConfirmationModalNew>
		)
	];
};

export default memo(SelectFacilityRequestShiftsModal);
